/**
 * App Settings - Application-specific configuration stored in Gobodo AppConfig__c.
 * Edit workflow stages, product types, and other per-app config.
 */

import React, { useState } from 'react';
import { Settings, Save, ChevronDown, ChevronRight } from 'lucide-react';
import { StandardButton, StandardTextarea } from '../standard';
import { useAppConfigs, useSaveAppConfig } from '../../services/queries/appConfigQueries';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import type { ApplicationId } from '../../services/queries/appConfigQueries';

const APPS: { id: ApplicationId; label: string }[] = [
  { id: 'cybersweep', label: 'CyberSweep' },
  { id: 'credit_management', label: 'Credit Management' },
  { id: 'credit_block', label: 'Credit Block' },
];

const DEFAULT_WORKFLOW_STAGES = [
  'Pending Enrollments',
  'Lacking Requirements',
  'Awaiting Assignment',
  'Pending Checklists',
  'Awaiting Initial Report',
  'Awaiting Final Report',
  'Quarterly Report 1',
  'Quarterly Report 2',
  'Quarterly Report 3',
  'Quarterly Report 4',
];

export const AppSettingsHome: React.FC = () => {
  const [expandedApp, setExpandedApp] = useState<ApplicationId | null>('cybersweep');
  const [stagesByApp, setStagesByApp] = useState<Record<string, string>>({});
  const { data: configs = [] } = useAppConfigs();
  const saveConfig = useSaveAppConfig();

  if (!USE_GOBODO_API) {
    return (
      <div className="p-6">
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <p className="text-amber-800 dark:text-amber-200">
            App Settings require Gobodo API. Enable REACT_APP_USE_GOBODO_API to edit application configuration.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
            <Settings className="w-8 h-8 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">App Settings</h1>
            <p className="text-gray-600 dark:text-gray-300">
              Configure workflow stages, product types, and other app-specific options
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {APPS.map((app) => {
          const isExpanded = expandedApp === app.id;
          const workflowConfig = configs.find(
            (r: Record<string, unknown>) =>
              (r.Application__c ?? r.application) === app.id &&
              (r.ConfigKey__c ?? r.configKey) === 'workflow_stages'
          ) as Record<string, unknown> | undefined;
          const configId = workflowConfig?.id as string | undefined;
          const currentStages = workflowConfig
            ? (() => {
                const json = workflowConfig.ConfigValueJson__c ?? workflowConfig.configValueJson;
                if (typeof json !== 'string') return [];
                try {
                  const parsed = JSON.parse(json);
                  return Array.isArray(parsed) ? parsed : [];
                } catch {
                  return [];
                }
              })()
            : DEFAULT_WORKFLOW_STAGES;

          return (
            <div
              key={app.id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
            >
              <button
                onClick={() => setExpandedApp(isExpanded ? null : app.id)}
                className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <span className="font-medium text-gray-900 dark:text-gray-100">{app.label}</span>
                {isExpanded ? (
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-gray-500" />
                )}
              </button>
              {isExpanded && (
                <div className="px-6 pb-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="space-y-4">
                    {app.id === 'cybersweep' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Workflow Stages
                        </label>
                        <StandardTextarea
                          rows={6}
                          value={stagesByApp[app.id] ?? currentStages.join('\n')}
                          onChange={(e) => setStagesByApp((s) => ({ ...s, [app.id]: e.target.value }))}
                          placeholder="One stage per line"
                        />
                        <StandardButton
                          variant="primary"
                          size="sm"
                          leftIcon={<Save className="h-4 w-4" />}
                          className="mt-2"
                          onClick={() => {
                            const lines = (stagesByApp[app.id] ?? currentStages.join('\n'))
                              .split('\n')
                              .map((s) => s.trim())
                              .filter(Boolean);
                            saveConfig.mutate({
                              applicationId: app.id,
                              configKey: 'workflow_stages',
                              configValue: lines,
                              id: configId,
                            });
                          }}
                        >
                          Save
                        </StandardButton>
                      </div>
                    )}
                    {app.id !== 'cybersweep' && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Additional configuration options coming soon.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
